import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute } from './context/AuthContext';

// Layout
import Layout from './components/layout/Layout';

// Auth Pages
import LoginPage from './pages/LoginPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import AdminResellers from './pages/admin/AdminResellers';
import AdminClients from './pages/admin/AdminClients';
import AdminPlans from './pages/admin/AdminPlans';
import AdminFeatures from './pages/admin/AdminFeatures';
import AdminCommissions from './pages/admin/AdminCommissions';
import AdminActivity from './pages/admin/AdminActivity';

// Reseller Pages
import ResellerDashboard from './pages/reseller/ResellerDashboard';
import ResellerClients from './pages/reseller/ResellerClients';
import ResellerCreateClient from './pages/reseller/ResellerCreateClient';
import ResellerCommissions from './pages/reseller/ResellerCommissions';

// Client Pages
import ClientDashboard from './pages/client/ClientDashboard';
import ClientEngine from './pages/client/ClientEngine';
import ClientSubscription from './pages/client/ClientSubscription';
import ClientUsage from './pages/client/ClientUsage';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout>
                  <Routes>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="resellers" element={<AdminResellers />} />
                    <Route path="clients" element={<AdminClients />} />
                    <Route path="plans" element={<AdminPlans />} />
                    <Route path="features" element={<AdminFeatures />} />
                    <Route path="commissions" element={<AdminCommissions />} />
                    <Route path="activity" element={<AdminActivity />} />
                    <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Reseller Routes */}
          <Route
            path="/reseller/*"
            element={
              <ProtectedRoute allowedRoles={['reseller']}>
                <Layout>
                  <Routes>
                    <Route path="dashboard" element={<ResellerDashboard />} />
                    <Route path="clients" element={<ResellerClients />} />
                    <Route path="create-client" element={<ResellerCreateClient />} />
                    <Route path="commissions" element={<ResellerCommissions />} />
                    <Route path="*" element={<Navigate to="/reseller/dashboard" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Client Routes */}
          <Route
            path="/client/*"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <Layout>
                  <Routes>
                    <Route path="dashboard" element={<ClientDashboard />} />
                    <Route path="engine" element={<ClientEngine />} />
                    <Route path="subscription" element={<ClientSubscription />} />
                    <Route path="usage" element={<ClientUsage />} />
                    <Route path="*" element={<Navigate to="/client/dashboard" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Default redirect based on role */}
          <Route path="/" element={<RoleBasedRedirect />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

// Component to redirect based on user role
const RoleBasedRedirect: React.FC = () => {
  // This will be handled by the AuthContext
  // For now, redirect to login
  return <Navigate to="/login" replace />;
};

export default App;
